import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { NumberInputField, Button, L2PayModal, ModalStepProps } from '@/components';
import { SETTLEMENT_ADDRESS } from '@/consts';
import { useCurrentUserId, formatNum } from '@/utils';

import { useOfferBoost } from '../../../hooks';
import styles from './index.module.scss';

type Props = { offerId: string; onClose: () => void };

export const BoostTipModal = ({ offerId, onClose }: Props) => {
  const [amount, setAmount] = useState<number>();
  const currentAddress = useCurrentUserId();
  const { mutateAsync, isLoading } = useOfferBoost(offerId);

  const _price = useMemo(() => `$${formatNum(amount)}`, [amount]);

  const txParams = useMemo(
    () =>
      amount && currentAddress
        ? {
            price: amount * 10000,
            message: `Pay ${_price} for Hi offer boost`,
            senderAddr: currentAddress,
            receiverAddr: SETTLEMENT_ADDRESS,
          }
        : undefined,
    [currentAddress, _price, amount],
  );

  const payTitle = useMemo(
    () => (
      <div className={styles.payTitle}>
        <Trans i18nKey="BoostTipModal.payTitleTrans" values={{ price: _price }}>
          Pay <span className={styles.payTitleWeight}>$0</span> for Hi offer boost
        </Trans>
      </div>
    ),
    [_price],
  );

  const handlePaySubmit = useCallback(
    async ({ tx }) => {
      await mutateAsync(tx);
      onClose();
    },
    [mutateAsync, onClose],
  );

  return (
    <L2PayModal
      turnOff={onClose}
      txParams={txParams}
      payTitle={payTitle}
      size="medium"
      isConfimLoading={isLoading}
      onPaySubmit={handlePaySubmit}>
      <BoostTipCard setAmount={setAmount} onClose={onClose} />
    </L2PayModal>
  );
};

const BoostTipCard = ({
  showNext,
  setAmount,
  onClose,
}: ModalStepProps<null> & { setAmount: (_?: number) => void }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
  });

  const onConfirm = useCallback(
    async data => {
      try {
        if (data.price) {
          setAmount(Number(data.price));
          showNext?.();
        }
      } catch (error) {}
    },
    [setAmount, showNext],
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>{t('BoostTipModal.8uqgm7')}</div>
      <div className={styles.inputWrap}>
        <span className={styles.prefix}>$</span>
        <NumberInputField
          inputClassName={styles.input}
          placeholder={t('BoostTipModal.4vjbx6')}
          rules={{
            required: true,
            min: {
              value: 1,
              message: t('BoostTipModal.4vjbx6'),
            },
            pattern: {
              value: /^\d+$/,
              message: t('BoostTipModal.1jffb0s'),
            },
          }}
          control={control}
          name="price"
        />
      </div>
      <div className={styles.desc}>{t('BoostTipModal.1ko32w1')}</div>
      <div className={styles.btns}>
        <Button className={styles.btn} onClick={onClose}>
          {t('BoostTipModal.x9d2fu')}
        </Button>
        <Button
          className={styles.btn}
          onClick={handleSubmit(onConfirm)}
          type="primary"
          disabled={!isValid}>
          {t('BoostTipModal.1fqbo1c')}
        </Button>
      </div>
      <div className={styles.footer}>{t('BoostTipModal.1ho0bx6')}</div>
    </div>
  );
};
